import type {
  DensitySnapshot,
  InstrumentRef,
  InstrumentStatusView,
  WrappedCoin,
} from "@screener/protocol";
import { Table, TableBody } from "@screener/uikit/components/table";
import { defaultRangeExtractor, type Range, useVirtualizer } from "@tanstack/react-virtual";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { DensityRowItem, rowAnnotations } from "./density-row-item";
import { DensityTableHeader } from "./density-table-header";
import { type DensitySort, densityEmptyMessage } from "./derive";
import { registerRowRevealer } from "./focus-handoff";
import { LIST_SCROLL_ATTRIBUTE } from "./list-scroll";
import type { RowPinMatch } from "./pin-match";
import { FALLBACK_REM_PX, rootFontPx } from "./root-font";
import { tableRowHeightPx, windowParts, withPinnedRows } from "./table-window";
import { useRowNavigation } from "./use-row-navigation";

const EMPTY_PENDING_PIN_IDS: ReadonlySet<string> = new Set();
const EMPTY_PENDING_FAVORITE_BASES: ReadonlySet<string> = new Set();

/** Запас строк выше и ниже окна прокрутки: прокрутка колесом не показывает пустоты. */
const OVERSCAN_ROWS = 10;

/** Высота окна при статическом рендере без DOM — короткие таблицы рисуются целиком (Д7). */
const STATIC_VIEWPORT_PX = 1080;

/** Сколько ждать, пока строка — цель переноса фокуса — нарисуется после прокрутки к ней (Д6). */
const REVEAL_FOCUS_TIMEOUT_MS = 1000;

/**
 * Ширины колонок при `table-layout: fixed` (Д3): по естественной ширине
 * содержимого и заголовка со стрелкой сортировки. Без явных ширин колонки
 * следовали бы за содержимым нарисованных строк и прыгали при прокрутке.
 * «Состояние» вмещает пометку «без данных», «Флаги» — три флага ММ (четыре
 * разом редки и обрезаются). `null` — «Пара»: она берёт остаток ширины.
 * Порядок — порядок ячеек строки: полоса стороны, одиннадцать колонок шапки,
 * действия. Сумма заданных — 77,625rem.
 */
const COLUMN_WIDTHS: readonly (string | null)[] = [
  "0.375rem",
  "7rem",
  null,
  "4.75rem",
  "9rem",
  "6rem",
  "8.5rem",
  "2.75rem",
  "7.5rem",
  "10rem",
  "3.5rem",
  "10rem",
  "8.25rem",
];

function initialViewportPx(): number {
  return typeof window === "undefined" ? STATIC_VIEWPORT_PX : window.innerHeight;
}

interface ScrollFrame {
  scrollElement: HTMLElement;
  /** Отступ тела таблицы от начала содержимого области прокрутки — высота шапки. */
  scrollMargin: number;
  remPx: number;
}

interface DensityTableProps {
  rows: DensitySnapshot[];
  /** Сколько строк было до фильтров — чтобы отличить «не пропустили» от «нет данных». */
  totalRows: number;
  /** Текущая сортировка — шапка показывает её стрелкой, меняется она на панели фильтров. */
  sort: DensitySort;
  instruments: InstrumentStatusView[];
  /** Перечень обёрнутых монет из приветствия. */
  wrapped: readonly WrappedCoin[];
  /** Приветствие ещё не пришло: таблицы нет вовсе, а не она пуста. */
  awaitingHello: boolean;
  /**
   * «Только избранные» включён, а список избранного ещё не прочитан или
   * недоступен: пустая таблица в этом случае не значит «избранных плотностей
   * нет» (спека `web-density-table` «Избранное не прочитано»).
   */
  favoritesUnavailable: boolean;
  /** Открыть детальную панель §9 по строке. */
  onOpenRow: (row: DensitySnapshot) => void;
  /**
   * Монеты, у которых есть хотя бы одна заметка §6.4 (сопоставление клиента).
   * `null` — список заметок ещё не прочитан или недоступен: строка не вправе
   * в этом случае утверждать «заметок нет» (review stage5-notes-and-pin, п. 3).
   */
  notedBases: Set<string> | null;
  /**
   * Закрепление §6.7, сопоставленное со строкой по `density_id`
   * (`matchPinsToRows`). `null` — тот же смысл, что и у `notedBases`.
   */
  pinMatches: Map<string, RowPinMatch> | null;
  /** Открыть детальную панель на разделе заметок §9. */
  onOpenNotes: (row: DensitySnapshot) => void;
  /** Закрепить уровень строки или снять уже стоящее закрепление. */
  onTogglePin: (row: DensitySnapshot) => void;
  /** Строки, чей собственный запрос закрепления ещё не завершился. */
  pendingPinIds?: ReadonlySet<string>;
  /**
   * Монеты, отмеченные избранным целиком §6.6 (сопоставление клиента).
   * `null` — тот же смысл, что и у `notedBases`: список ещё не прочитан или
   * недоступен, и строка не вправе утверждать «не в избранном».
   */
  favoriteBases: Set<string> | null;
  /** Поставить или снять избранное монеты строки целиком. */
  onToggleFavorite: (row: DensitySnapshot) => void;
  /** Монеты, чьё собственное действие избранного ещё не завершилось. */
  pendingFavoriteBases?: ReadonlySet<string>;
  /** Скрыть монету целиком — хоткей `h` строки (§75 п. 2). */
  onHideCoin: (base: string) => void;
  /** Скопировать тикер строки в буфер обмена (§9 1.44, `tickerForClipboard`). */
  onCopyTicker: (row: DensitySnapshot) => void;
}

function instrumentKey(instrument: InstrumentRef): string {
  return `${instrument.exchange}:${instrument.market}:${instrument.base}${instrument.quote}`;
}

/**
 * Живая таблица плотностей §9. Сортировок ровно три — объём в USD, время
 * жизни и дистанция; ликва за плотностью строки не сортирует (§9 1.16).
 */
export function DensityTable({
  rows,
  totalRows,
  sort,
  instruments,
  wrapped,
  awaitingHello,
  favoritesUnavailable,
  onOpenRow,
  notedBases,
  pinMatches,
  onOpenNotes,
  onTogglePin,
  pendingPinIds = EMPTY_PENDING_PIN_IDS,
  favoriteBases,
  onToggleFavorite,
  pendingFavoriteBases = EMPTY_PENDING_FAVORITE_BASES,
  onHideCoin,
  onCopyTicker,
}: DensityTableProps) {
  // Роверный `tabindex`: точка табуляции в таблице ровно одна, между строками
  // ходят стрелками. Хелпер общий со списком ситуаций (§9 этап 5, шаг 3.1) —
  // идентичность строки здесь ровно та же, `density_id`. Шаг по ключу, а не по
  // соседству в DOM: между нарисованными строками стоят распорки (Д5).
  const {
    focusable,
    onFocus,
    onKeyDown: onRowKeyDown,
    activeIndex,
  } = useRowNavigation(
    rows.map((row) => row.density_id),
    { step: "keys" },
  );

  // Область прокрутки, отступ тела таблицы в ней и корневой шрифт меряются в
  // layout-эффекте; дальше за ними следит `ResizeObserver` шапки: её высота
  // в rem и меняется вместе со шрифтом.
  const [table, setTable] = useState<HTMLTableElement | null>(null);
  const [frame, setFrame] = useState<ScrollFrame | null>(null);
  useLayoutEffect(() => {
    const scrollElement = table?.closest<HTMLElement>(`[${LIST_SCROLL_ATTRIBUTE}]`) ?? null;
    const body = table?.tBodies[0];
    if (table === null || scrollElement === null || body === undefined) {
      return;
    }
    const measure = () => {
      const scrollMargin =
        body.getBoundingClientRect().top -
        scrollElement.getBoundingClientRect().top +
        scrollElement.scrollTop;
      const remPx = rootFontPx();
      setFrame((previous) =>
        previous !== null &&
        previous.scrollElement === scrollElement &&
        previous.scrollMargin === scrollMargin &&
        previous.remPx === remPx
          ? previous
          : { scrollElement, scrollMargin, remPx },
      );
    };
    measure();
    const head = table.tHead;
    if (typeof ResizeObserver === "undefined" || head === null) {
      return;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(head);
    return () => observer.disconnect();
  }, [table]);

  const rowHeight = tableRowHeightPx(frame?.remPx ?? FALLBACK_REM_PX);
  // Точка табуляции и её соседи рисуются всегда (Д4): иначе таб проскакивает
  // таблицу после прокрутки, а строка с фокусом, уехавшая за окно, теряет его.
  const rangeExtractor = useCallback(
    (range: Range) => withPinnedRows(defaultRangeExtractor(range), activeIndex, range.count),
    [activeIndex],
  );
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => frame?.scrollElement ?? null,
    estimateSize: () => rowHeight,
    overscan: OVERSCAN_ROWS,
    scrollMargin: frame?.scrollMargin ?? 0,
    rangeExtractor,
    initialRect: { width: 0, height: initialViewportPx() },
  });
  // Высота строки задана оценкой, а не замером: сменился шрифт — оценки
  // пересчитываются.
  useEffect(() => {
    if (rowHeight > 0) {
      virtualizer.measure();
    }
  }, [virtualizer, rowHeight]);

  // Перенос фокуса после скрытия монеты к строке за окном (Д6): прокрутить к
  // ней и сфокусировать, когда она нарисуется. Ожидание ограничено сроком:
  // строка, не нарисовавшаяся сразу после прокрутки, не должна отнять фокус
  // позже, когда человек сам докрутит до неё.
  const pendingFocusRef = useRef<{ key: string; untilMs: number } | null>(null);
  const latestRef = useRef({ rows, virtualizer });
  latestRef.current = { rows, virtualizer };
  useEffect(
    () =>
      registerRowRevealer((key) => {
        const index = latestRef.current.rows.findIndex((row) => row.density_id === key);
        if (index < 0) {
          return false;
        }
        pendingFocusRef.current = { key, untilMs: performance.now() + REVEAL_FOCUS_TIMEOUT_MS };
        latestRef.current.virtualizer.scrollToIndex(index, { align: "center" });
        return true;
      }),
    [],
  );
  useEffect(() => {
    const pending = pendingFocusRef.current;
    if (pending === null || table === null) {
      return;
    }
    if (
      performance.now() > pending.untilMs ||
      !rows.some((row) => row.density_id === pending.key)
    ) {
      pendingFocusRef.current = null;
      return;
    }
    const target = table.querySelector(`[data-row-key="${CSS.escape(pending.key)}"]`);
    if (!(target instanceof HTMLElement)) {
      return;
    }
    pendingFocusRef.current = null;
    // Фокус, который человек успел поставить сам — в поле, в панель, — не отнимается.
    const active = document.activeElement;
    if (active === null || active === document.body) {
      target.focus({ preventScroll: true });
    }
  });

  // Состав устаревших инструментов меняется со статусом, а не с каждым кадром.
  const staleInstruments = useMemo(
    () =>
      new Set(
        instruments
          .filter((entry) => entry.status !== "live")
          .map((entry) => instrumentKey(entry.instrument)),
      ),
    [instruments],
  );
  const annotationSources = {
    notedBases,
    pinMatches,
    pendingPinIds,
    favoriteBases,
    pendingFavoriteBases,
  };

  const emptyMessage = densityEmptyMessage({ awaitingHello, totalRows, favoritesUnavailable });

  return (
    <>
      <Table
        ref={setTable}
        data-row-list
        // Ширина таблицы не меньше суммы колонок и семи rem на пару: уже —
        // горизонтальная прокрутка обёртки, а не сжатые колонки (Д3).
        className="min-w-[84.625rem] table-fixed text-[13px] [&_td]:overflow-hidden [&_td]:text-ellipsis [&_th]:overflow-hidden [&_th]:text-ellipsis"
      >
        <colgroup>
          {COLUMN_WIDTHS.map((width, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: состав колонок постоянен
            <col key={index} style={width === null ? undefined : { width }} />
          ))}
        </colgroup>
        <DensityTableHeader showsSort sort={sort} />

        <TableBody>
          {windowParts(
            virtualizer.getVirtualItems(),
            frame?.scrollMargin ?? 0,
            virtualizer.getTotalSize(),
          ).map((part) => {
            if (part.kind === "spacer") {
              // Распорка на месте непоказанных строк: только высота, без
              // содержимого и без `data-row-key` — навигация её не видит, а
              // читалка экрана не считает её строкой.
              return (
                // biome-ignore lint/a11y/noAriaHiddenOnFocusable: распорка без tabindex и содержимого, фокуса не получает
                <tr key={part.key} aria-hidden="true" style={{ height: part.height }}>
                  <td colSpan={COLUMN_WIDTHS.length} className="p-0" />
                </tr>
              );
            }
            const row = rows[part.index];
            if (row === undefined) {
              return null;
            }
            return (
              <DensityRowItem
                key={row.density_id}
                row={row}
                wrapped={wrapped}
                stale={staleInstruments.has(instrumentKey(row.instrument))}
                focusable={focusable(row.density_id)}
                {...rowAnnotations(row, annotationSources)}
                onFocus={onFocus}
                onKeyDown={onRowKeyDown}
                onOpenRow={onOpenRow}
                onOpenNotes={onOpenNotes}
                onTogglePin={onTogglePin}
                onToggleFavorite={onToggleFavorite}
                onHideCoin={onHideCoin}
                onCopyTicker={onCopyTicker}
              />
            );
          })}
        </TableBody>
      </Table>
      {/* Пустое состояние — вне горизонтально прокручиваемой таблицы: иначе оно
        центрируется по её полной ширине и на узком окне уходит за край. */}
      {rows.length === 0 ? (
        <p className="flex min-h-20 items-center justify-center px-4 py-3 text-center text-[13px] text-balance text-muted-foreground">
          {emptyMessage}
        </p>
      ) : null}
    </>
  );
}
